import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Home from "./pages/Home";
import About from "./pages/About";
import FAQ from "./pages/FAQ";
import Contact from "./pages/Contact";
import Auth from "./pages/Auth";
import BookInspection from "./pages/BookInspection";
import AdminDashboard from "./pages/AdminDashboard";
import InspectionForm from "./pages/InspectionForm";
import ViewReport from "./pages/ViewReport";
import SampleReport from "./pages/SampleReport";
import StatusPage from "./pages/StatusPage";
import WhatYouReceive from "./pages/WhatYouReceive";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import AcademyHome from "./pages/academy/AcademyHome";
import AcademyCategory from "./pages/academy/AcademyCategory";
import AcademyAllArticles from "./pages/academy/AcademyAllArticles";
import AcademyVideos from "./pages/academy/AcademyVideos";
import AcademyArticle from "./pages/academy/AcademyArticle";
import FirstLookForm from "./pages/FirstLookForm";
import FirstLookLanding from "./pages/FirstLookLanding";
import FirstLookResult from "./pages/FirstLookResult";
import FirstLookLeads from "./pages/FirstLookLeads";
import AdminInspections from "./pages/AdminInspections";
import LocationsIndex from "./pages/LocationsIndex";
import LocationPage from "./pages/LocationPage";
import ServicesIndex from "./pages/ServicesIndex";
import ServicePage from "./pages/ServicePage";
import RoadTripReady from "./pages/RoadTripReady";
import RecentInspections from "./pages/RecentInspections";
import OutOfStateInspection from "./pages/OutOfStateInspection";
import VehiclesWeInspect from "./pages/VehiclesWeInspect";
import UsedCarBuyerToolkit from "./pages/UsedCarBuyerToolkit";
import ToolkitAccess from "./pages/ToolkitAccess";
import MobilePrePurchaseInspection from "./pages/MobilePrePurchaseInspection";
import CarvanaInspection from "./pages/CarvanaInspection";
import CarInspectionNegotiation from "./pages/CarInspectionNegotiation";
import { Toaster } from "@/components/ui/sonner";
import GlobalMenu from "./components/GlobalMenu";
import PromoBanner from "./components/PromoBanner";
import StickyBookButton from "./components/StickyBookButton";
import Analytics from "./components/Analytics";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0a1828 0%, #1a2332 50%, #0f1923 100%)' }}>
        <p className="text-white">Loading...</p>
      </div>
    );
  }
  
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Admin-only Route Component
const AdminRoute = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0a1828 0%, #1a2332 50%, #0f1923 100%)' }}>
        <p className="text-white">Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: { pathname: '/admin' } }} />;
  }

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: 'linear-gradient(135deg, #0a1828 0%, #1a2332 50%, #0f1923 100%)' }}>
        <div className="text-center">
          <p className="text-white text-lg mb-2">Access denied</p>
          <p className="text-gray-400">This area is restricted to CarLens administrators.</p>
        </div>
      </div>
    );
  }

  return children;
};

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/about" element={<About />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/login" element={<Auth />} />
      <Route path="/book" element={<BookInspection />} />
      <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="/admin/firstlook" element={<AdminRoute><FirstLookLeads /></AdminRoute>} />
      <Route path="/admin/inspections" element={<AdminRoute><AdminInspections /></AdminRoute>} />
      <Route path="/firstlook" element={<FirstLookLanding />} />
      <Route path="/firstlook/start" element={<FirstLookForm />} />
      <Route path="/firstlook/:id" element={<FirstLookResult />} />
      <Route path="/inspection/:bookingId" element={<InspectionForm />} />
      <Route path="/report/:bookingId" element={<ViewReport />} />
      <Route path="/sample-report" element={<SampleReport />} />
      <Route path="/status/:bookingId" element={<StatusPage />} />
      <Route path="/what-you-receive" element={<WhatYouReceive />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/academy" element={<AcademyHome />} />
      <Route path="/academy/articles" element={<AcademyAllArticles />} />
      <Route path="/academy/videos" element={<AcademyVideos />} />
      <Route path="/academy/category/:slug" element={<AcademyCategory />} />
      <Route path="/academy/:slug" element={<AcademyArticle />} />
      <Route path="/locations" element={<LocationsIndex />} />
      <Route path="/locations/:slug" element={<LocationPage />} />
      <Route path="/services" element={<ServicesIndex />} />
      <Route path="/services/:slug" element={<ServicePage />} />
      <Route path="/road-trip-ready" element={<RoadTripReady />} />
      <Route path="/recent-inspections" element={<RecentInspections />} />
      <Route path="/out-of-state-car-inspection" element={<OutOfStateInspection />} />
      <Route path="/vehicles-we-inspect" element={<VehiclesWeInspect />} />
      <Route path="/used-car-buyer-toolkit" element={<UsedCarBuyerToolkit />} />
      <Route path="/toolkit-access" element={<ToolkitAccess />} />
      <Route path="/mobile-pre-purchase-inspection-jacksonville" element={<MobilePrePurchaseInspection />} />
      <Route path="/carvana-inspection-jacksonville" element={<CarvanaInspection />} />
      <Route path="/car-inspection-negotiation" element={<CarInspectionNegotiation />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <ScrollToTop />
            <Analytics />
            <PromoBanner />
            <GlobalMenu />
            <AppRoutes />
            <StickyBookButton />
            <Toaster position="top-right" />
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;